import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Theme values: 'dark' | 'light' | 'auto'
// 'auto' follows the OS-level prefers-color-scheme media query.

const MEDIA_QUERY = typeof window !== 'undefined'
  ? window.matchMedia('(prefers-color-scheme: dark)')
  : null;

function getSystemTheme() {
  return MEDIA_QUERY && MEDIA_QUERY.matches ? 'dark' : 'light';
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  const effective = theme === 'auto' ? getSystemTheme() : theme;
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else {
    root.classList.remove('dark');
    root.classList.add('light');
  }
}

export const PALETTES = [
  { id: 'defaut', nom: 'Actuelle', desc: 'Brun chaud / terracotta', couleurs: ['#191410', '#E2704A', '#3FB27F'] },
  { id: 'chaleureux', nom: 'Rétail chaleureux', desc: 'Crème / terracotta / vert sapin', couleurs: ['#F8F4EC', '#BF5730', '#2A6A4E'] },
  { id: 'corporate', nom: 'Corporate moderne', desc: 'Bleu nuit / cyan électrique', couleurs: ['#08101F', '#22D3EE', '#10B981'] },
  { id: 'distribution', nom: 'Grande distribution', desc: 'Blanc / rouge enseigne / ardoise', couleurs: ['#F1F3F5', '#D91E2E', '#37424F'] },
  { id: 'premium', nom: 'Premium boutique', desc: 'Charbon / or / bordeaux', couleurs: ['#101013', '#D4AF37', '#8C2438'] },
];

const PALETTE_CLASSES = ['theme-chaleureux', 'theme-corporate', 'theme-distribution', 'theme-premium'];

function applyPaletteClass(palette) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  PALETTE_CLASSES.forEach((c) => root.classList.remove(c));
  if (palette && palette !== 'defaut') root.classList.add(`theme-${palette}`);
}

const useThemeStore = create(
  persist(
    (set, get) => ({
      // Default to dark to preserve the existing experience for current users
      theme: 'dark',

      // Cycle: dark → light → auto → dark...
      cycleTheme: () => {
        const order = ['dark', 'light', 'auto'];
        const i = order.indexOf(get().theme);
        const next = order[(i + 1) % order.length];
        set({ theme: next });
        applyThemeClass(next);
      },

      setTheme: (theme) => {
        set({ theme });
        applyThemeClass(theme);
      },

      // Palette de couleurs de la simulation ('defaut' | 'chaleureux' | 'corporate' | 'distribution' | 'premium')
      palette: 'defaut',
      setPalette: (palette) => {
        set({ palette });
        applyPaletteClass(palette);
      },

      // Returns the actually-applied theme (resolves 'auto' to dark/light)
      effectiveTheme: () => {
        const t = get().theme;
        return t === 'auto' ? getSystemTheme() : t;
      },
    }),
    {
      name: 'hst-theme',
      onRehydrateStorage: () => (state) => {
        if (state?.theme) applyThemeClass(state.theme);
        else applyThemeClass('dark');
        applyPaletteClass(state?.palette || 'defaut');
      },
    }
  )
);

// Listen to OS-level theme changes when in 'auto' mode
if (MEDIA_QUERY) {
  MEDIA_QUERY.addEventListener('change', () => {
    if (useThemeStore.getState().theme === 'auto') {
      applyThemeClass('auto');
    }
  });
}

// Pre-apply on first paint to avoid flash
if (typeof window !== 'undefined') {
  try {
    const stored = JSON.parse(localStorage.getItem('hst-theme') || '{}');
    const initial = stored?.state?.theme || 'dark';
    applyThemeClass(initial);
    applyPaletteClass(stored?.state?.palette || 'defaut');
  } catch {
    applyThemeClass('dark');
  }
}

export default useThemeStore;
